import { datadogLogs } from "@datadog/browser-logs";

/**
 * Authenticated binary download helper.
 *
 * <p>Every QQT endpoint except the public ones requires authentication. Since the SSO migration
 * the primary credential is the HttpOnly `QQT_SESSION` cookie issued by the backend BFF, which the
 * browser only attaches when the request opts in with `credentials: "include"` — omitting it is
 * what produced 403s on the file/quote download links. The legacy eGerdau bearer token is still
 * sent when one is present so environments that have not retired eGerdau keep working, but it is
 * skipped when empty to avoid an `Authorization: Bearer ` header that fails token validation.</p>
 */
export const downloadFileWithSession = async (uri: string, token?: string) => {
  const headers = new Headers();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  const response = await fetch(uri, {
    method: "GET",
    mode: "cors",
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    throw new Error(`Failed to download file (${response.status})`);
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const disposition = response.headers.get("Content-Disposition");
  if (disposition && disposition.includes("attachment")) {
    const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      link.download = filenameMatch[1].replace(/['"]/g, "");
    }
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** Fire-and-forget wrapper used by download links: logs failures instead of throwing. */
export const downloadFileWithSessionSafely = async (
  uri: string,
  token?: string,
  context?: string,
) => {
  try {
    await downloadFileWithSession(uri, token);
  } catch (error) {
    datadogLogs.logger.error("[Download]: Error downloading file", {
      name: context ?? "Download error",
      details: { uri, error },
    });
    console.error("Error downloading file:", error);
  }
};
